import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import { Checkbox } from '@epoch-ui/components/Checkbox';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage
} from '@epoch-ui/components/Form/form';

// TODO: Use src/epoch-ui/components/Form/fields/CheckboxGroupField/CheckboxGroupField.tsx instead and delete this
export interface CheckboxGroupFieldProps {
  name: string;
  label?: string | React.ReactNode;
  description?: string | React.ReactNode;
  className?: string;
  disabled?: boolean;
  options: string[];
  onValueChange?: (value: string[]) => void;
  children?: React.ReactNode;
}

export const CheckboxGroupField = React.forwardRef<HTMLDivElement, CheckboxGroupFieldProps>(
  ({ name, label, description, className, disabled, options, onValueChange, children }, ref) => {
    const formCtx = useFormContext();
    return (
      <FormField
        control={formCtx.control}
        name={name}
        render={({ field }) => {
          const currentValue = (field.value as string[] | undefined) || [];
          const toggleOption = (option: string, isChecked: boolean): void => {
            const newValues = isChecked
              ? [...currentValue, option]
              : currentValue.filter((v) => v !== option);
            field.onChange(newValues);
            onValueChange?.(newValues);
          };
          return (
            <FormItem ref={ref}>
              {label && (
                <FormLabel className={cn('tw-mb-4', disabled && 'tw-opacity-40')}>
                  {label}
                </FormLabel>
              )}
              {description && (
                <FormDescription className={cn('tw-m-0', disabled && 'tw-opacity-40')}>
                  {description}
                </FormDescription>
              )}
              <FormControl>
                <div className={cn('tw-flex tw-flex-col', className)}>
                  {options.map((option) => {
                    const isChecked = currentValue.includes(option);

                    return (
                      <label
                        key={option}
                        className={cn(
                          'tw-mb-2 tw-flex tw-items-center tw-gap-3',
                          disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
                        )}
                      >
                        <Checkbox
                          checked={isChecked}
                          disabled={disabled}
                          onCheckedChange={(checked) => toggleOption(option, !!checked)}
                        />
                        <span
                          className={cn('tw-text-sm tw-text-black', disabled && 'tw-opacity-40')}
                        >
                          {option}
                        </span>
                      </label>
                    );
                  })}
                  {children}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    );
  }
);

CheckboxGroupField.displayName = 'CheckboxGroupField';
